import React, { Component } from "react";
import { Table, Button, Progress, Alert, Card, CardBody, CardHeader } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Select from "react-select";
import {
  API_URL_partners,
  API_URL_utilidad,
  API_URL_utilidad_partner_curso_abierto,
} from "../../../constants";
import { formatNumber } from "../../utils";
import {
  recomputeAsignacionUtilidades,
  isExcludedFromAssignment,
  getAssignmentPoolPercent,
  getCoachingPercent,
} from "../../../utils/asignacion-utilidad";
import { getAuthHeaders, getCurrentUser } from "../../../utils/auth";
import emailjs from "@emailjs/browser";
import * as XLSX from "xlsx";

// Map legacy / label variants coming from DB to our internal keys
const KEY_ALIASES = {
  "Contacto": "contacto",
  "Cita": "cita",
  "Diagnóstico": "diagnostico",
  "Diagnostico": "diagnostico",
  "Propuesta": "propuesta",
  "Cierre": "cierre",
  "Kickoff": "kickoff",
  "Seguimiento": "seguimiento",
  "Reunión de Cierre": "reunioncierre",
  "Reunion de Cierre": "reunioncierre",
  "ReunionCierre": "reunioncierre",
  "Coaching/Lider": "coaching",
  "Coaching": "coaching",
};

// Stage display names for UI
const STAGE_DISPLAY_NAMES = {
  contacto: "Contacto",
  cita: "Cita",
  diagnostico: "Diagnóstico",
  propuesta: "Propuesta",
  cierre: "Cierre",
  kickoff: "Kickoff",
  seguimiento: "Seguimiento",
  reunioncierre: "Reunión de Cierre",
  coaching: "Coaching/Líder",
};

class HonorariosList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      partners: [],
      honorarios: this.initializeHonorarios(props.proyectoLider),
      percent: this.initializePercent(HonorariosList.computeHasInstructors(props)),
      amount: this.initializeAmount(),
      isSaving: false,
      isRecalculating: false,
      sendingEmails: false,
      emailSendResult: null,
      utilidadRowMeta: {},
      totalPrecioByVendedor: {},
      totalPrecioAllVendedores: 0,
      hasAutoRecalculated: false,
    };
  }

  static computeHasInstructors(props) {
    return Array.isArray(props?.instructoresProportions) && props.instructoresProportions.length > 0;
  }

  hasInstructors = () => HonorariosList.computeHasInstructors(this.props);

  // ✅ Use shared auth helper
  getHeaders = () => {
    return getAuthHeaders();
  };

  initializeHonorarios(defaultLeader = "") {
    return {
      contacto: defaultLeader || "",
      cita: defaultLeader || "",
      diagnostico: defaultLeader || "",
      propuesta: defaultLeader || "",
      cierre: defaultLeader || "",
      kickoff: defaultLeader || "",
      seguimiento: defaultLeader || "",
      reunioncierre: defaultLeader || "",
      coaching: defaultLeader || "",
    };
  }

  initializePercent(hasInstructors = true) {
    return {
      contacto: 3.75,
      cita: 3.75,
      diagnostico: 2.5,
      propuesta: 5,
      cierre: 2.5,
      kickoff: 2.5,
      seguimiento: 2.5,
      reunioncierre: 2.5,
      coaching: getCoachingPercent(hasInstructors),
    };
  }

  initializeAmount() {
    return {
      contacto: 0,
      cita: 0,
      diagnostico: 0,
      propuesta: 0,
      cierre: 0,
      kickoff: 0,
      seguimiento: 0,
      reunioncierre: 0,
      coaching: 0,
    };
  }

  async componentDidMount() {
    const headers = this.getHeaders();

    try {
      const resp = await axios.get(API_URL_partners, { headers });
      const partners = (resp.data || [])
        .slice()
        .sort((a, b) => a.partner.localeCompare(b.partner));
      this.setState({ partners });

      await this.fetchUtilidadData();
      this.calculateTotalPrecios();
      this.recomputeAmountsFromUtilidad();

      // Auto-save leader defaults for stages that have no partner saved yet
      await this.autoSaveLeaderDefaults();

      // ✅ Auto-recalculate once on mount (with guard to prevent loops)
      if (!this.state.hasAutoRecalculated && this.props.utilidad > 0) {
        this.setState({ hasAutoRecalculated: true }, async () => {
          await this.handleRecalculateAssignments(true); // silent mode
        });
      }

      // Expose instance to parent via forwardedRef
      if (this.props.forwardedRef) {
        if (typeof this.props.forwardedRef === 'function') {
          this.props.forwardedRef(this);
        } else if (this.props.forwardedRef.current !== undefined) {
          this.props.forwardedRef.current = this;
        }
      }
    } catch (err) {
      console.error("Error in componentDidMount:", err);
    }
  }

  componentDidUpdate(prevProps) {
    const proyectoChanged = this.props.proyectoCodeSelected !== prevProps.proyectoCodeSelected;
    const utilidadChanged = this.props.utilidad !== prevProps.utilidad;
    const leaderChanged = this.props.proyectoLider !== prevProps.proyectoLider;

    const estudiantesChanged =
      JSON.stringify(this.props.estudiantes_curso_abierto) !==
      JSON.stringify(prevProps.estudiantes_curso_abierto);

    const prevHadInstructors = HonorariosList.computeHasInstructors(prevProps);
    const currHasInstructors = HonorariosList.computeHasInstructors(this.props);
    const instructorsToggled = prevHadInstructors !== currHasInstructors;

    if (proyectoChanged || leaderChanged) {
      this.resetStateWithLeader(this.props.proyectoLider);
    }

    if (utilidadChanged) {
      this.recomputeAmountsFromUtilidad();
    }

    if (estudiantesChanged) {
      this.calculateTotalPrecios();
    }

    if (instructorsToggled && !proyectoChanged && !leaderChanged) {
      this.applyCoachingPercentForInstructorState();
    }
  }

  applyCoachingPercentForInstructorState = () => {
    const newCoachingPercent = getCoachingPercent(this.hasInstructors());
    const nextPercent = { ...this.state.percent, coaching: newCoachingPercent };
    this.setState({ percent: nextPercent }, async () => {
      this.recomputeAmountsFromUtilidad();
      // Persist the new coaching percentage if a partner is already assigned there,
      // so the backend utilities row stays consistent with what the UI shows.
      if (this.state.honorarios.coaching && this.props.proyectoCodeSelected) {
        await this.handleSubmitForKeys(["coaching"]);
      }
    });
  };

  resetStateWithLeader = (leaderName) => {
    this.setState(
      {
        honorarios: this.initializeHonorarios(leaderName),
        percent: this.initializePercent(this.hasInstructors()),
        amount: this.initializeAmount(),
        hasAutoRecalculated: false,
      },
      async () => {
        await this.fetchUtilidadData();
        this.recomputeAmountsFromUtilidad();

        // Auto-save leader defaults for stages without a saved partner
        await this.autoSaveLeaderDefaults();

        // ✅ Auto-recalculate for the new project
        if (this.props.utilidad > 0) {
          this.setState({ hasAutoRecalculated: true }, async () => {
            await this.handleRecalculateAssignments(true); // silent mode
          });
        }
      }
    );
  };

  recomputeAmountsFromUtilidad = () => {
    const { utilidad } = this.props;
    const { percent } = this.state;

    const nextAmount = { ...this.state.amount };
    Object.keys(nextAmount).forEach((key) => {
      const pct = Number(percent[key]) || 0;
      nextAmount[key] = (Number(utilidad) || 0) * (pct / 100);
    });
    this.setState({ amount: nextAmount });
  };

  calculateTotalPrecios = () => {
    const { estudiantes_curso_abierto } = this.props;
    if (!Array.isArray(estudiantes_curso_abierto)) return;

    const totalPrecioByVendedor = {};
    let totalPrecioAllVendedores = 0;

    estudiantes_curso_abierto.forEach((est) => {
      const { miembro_vendedor, precio, moneda } = est || {};
      if (miembro_vendedor && precio != null) {
        if (!totalPrecioByVendedor[miembro_vendedor]) {
          totalPrecioByVendedor[miembro_vendedor] = { totalPrecio: 0, moneda };
        }
        totalPrecioByVendedor[miembro_vendedor].totalPrecio += Number(precio) || 0;
      }
    });

    totalPrecioAllVendedores = Object.values(totalPrecioByVendedor).reduce(
      (acc, v) => acc + (v.totalPrecio || 0),
      0
    );

    this.setState({ totalPrecioByVendedor, totalPrecioAllVendedores });
  };

  fetchUtilidadData = async () => {
    const headers = this.getHeaders();

    try {
      const resp = await axios.get(API_URL_utilidad, { headers });
      const rows = (resp.data || []).filter(
        (it) => it.proyecto_code === this.props.proyectoCodeSelected
      );

      if (!rows.length) return;

      const newAmount = { ...this.state.amount };
      const newPercent = { ...this.state.percent };
      const newHonorarios = { ...this.state.honorarios };
      const newMeta = { ...this.state.utilidadRowMeta };

      rows.forEach((item) => {
        const rawKey = item.tipo_utilidad || "";
        const key = KEY_ALIASES[rawKey] || rawKey;

        if (!(key in newAmount)) return;
        newHonorarios[key] = item.partner || newHonorarios[key];
        if (item.porcentaje != null) newPercent[key] = Number(item.porcentaje) || 0;
        if (item.cantidad != null) newAmount[key] = Number(item.cantidad) || 0;
        newMeta[key] = {
          id: item.pk,
          confirmation_status: item.confirmation_status || 'pending',
          confirmed_at: item.confirmed_at || null,
          confirm_yes_url: item.confirm_yes_url || null,
          confirm_no_url: item.confirm_no_url || null,
        };
      });

      this.setState(
        {
          amount: newAmount,
          percent: newPercent,
          honorarios: newHonorarios,
          utilidadRowMeta: newMeta,
        },
        () => {
          this.recomputeAmountsFromUtilidad();
          // Notify parent if coaching/leader value is loaded
          if (newHonorarios.coaching && this.props.onLeaderLoaded) {
            this.props.onLeaderLoaded(newHonorarios.coaching);
          }
        }
      );
    } catch (err) {
      console.error("Error fetching utilidad:", err);
    }
  };

  autoSaveLeaderDefaults = async () => {
    const leader = this.props.proyectoLider;
    if (!leader || !this.props.proyectoCodeSelected) return;

    const { honorarios, percent, amount } = this.state;
    const headers = this.getHeaders();

    // Find stages where the leader was set as default but no record exists in DB yet
    // (honorarios[key] === leader means it came from initializeHonorarios, not from fetchUtilidadData)
    const keysToSave = Object.keys(honorarios).filter((key) => {
      // Only save if the current value is the leader (from default) and amount is 0
      // (amount > 0 means it was loaded from DB)
      return honorarios[key] === leader && (Number(amount[key]) || 0) === 0;
    });

    if (keysToSave.length === 0) return;

    // Recompute amounts before saving
    const utilidad = Number(this.props.utilidad) || 0;

    for (const key of keysToSave) {
      const pct = Number(percent[key]) || 0;
      const cant = utilidad * (pct / 100);
      try {
        await axios.post(API_URL_utilidad, {
          proyecto_code: this.props.proyectoCodeSelected,
          tipo_utilidad: key,
          porcentaje: pct,
          cantidad: cant,
          partner: leader,
        }, { headers });
      } catch (err) {
        console.error(`Auto-save leader default failed for ${key}:`, err);
      }
    }

    // Refresh data after saving
    await this.fetchUtilidadData();
    this.recomputeAmountsFromUtilidad();
  };

  onSelectChange = (selectedOption, fieldName) => {
    const value = selectedOption ? selectedOption.value : "";

    const { honorarios } = this.state;
    this.setState(
      { honorarios: { ...honorarios, [fieldName]: value } },
      () => this.handleSubmitForKeys([fieldName])
    );
  };

  handleSubmitForKeys = async (keys) => {
    this.setState({ isSaving: true });
    const headers = this.getHeaders();

    const { honorarios, percent, amount } = this.state;
    let hadError = false;

    for (const key of keys) {
      const partner = honorarios[key];
      if (!partner) continue;
      const payload = {
        proyecto_code: this.props.proyectoCodeSelected,
        tipo_utilidad: key,
        porcentaje: Number(percent[key]) || 0,
        cantidad: Number(amount[key]) || 0,
        partner,
      };

      try {
        await axios.post(API_URL_utilidad, payload, { headers });
      } catch (err) {
        console.error(`POST utilidad failed for ${key}:`, err);
        hadError = true;
      }
    }

    if (hadError) {
      toast.error("Error al guardar datos. Favor de verificar.");
    } else {
      toast.success("Información guardada.");
    }
    this.setState({ isSaving: false });
  };

  // ✅ Recalculate instructor assignments (can be called manually or automatically)
  handleRecalculateAssignments = async (silent = false) => {
    const { proyectoCodeSelected } = this.props;

    if (!proyectoCodeSelected) {
      if (!silent) toast.warning("No hay proyecto seleccionado.");
      return;
    }

    this.setState({ isRecalculating: true });
    const headers = this.getHeaders();

    try {
      const result = await recomputeAsignacionUtilidades(proyectoCodeSelected, headers, {
        hasInstructors: this.hasInstructors(),
      });

      if (!silent) {
        if (result.success) {
          if (result.saved > 0) {
            toast.success(`✅ Asignaciones recalculadas: ${result.saved} partner(s) actualizados`);
          } else {
            toast.info(`ℹ️ Sin cambios: ${result.reason || "no hay datos para recalcular"}`);
          }
        } else {
          toast.error(`❌ Error al recalcular: ${result.reason || "error desconocido"}`);
        }
      }

      // Refresh data after recalculation
      await this.fetchUtilidadData();

    } catch (error) {
      console.error("Error recalculating assignments:", error);
      if (!silent) {
        toast.error("Error al recalcular asignaciones.");
      }
    } finally {
      this.setState({ isRecalculating: false });
    }
  };

  // ✅ Calculate instructor assignment commissions (12.5% of utilidad, distributed by income proportion)
  calculateInstructorAssignments = () => {
    const { instructoresProportions, utilidad } = this.props;

    if (!Array.isArray(instructoresProportions) || instructoresProportions.length === 0) {
      return { assignments: [], totalPool: 0, totalIncome: 0, excludedIncome: 0 };
    }

    let totalEligibleIncome = 0;
    let excludedIncome = 0;

    instructoresProportions.forEach((p) => {
      const partner = (p?.miembro_que_asigna || "").trim();
      const income = Number(p?.totalIncome) || 0;

      if (isExcludedFromAssignment(partner)) {
        excludedIncome += income;
      } else {
        totalEligibleIncome += income;
      }
    });

    const poolPercent = getAssignmentPoolPercent();
    const assignmentPool = (Number(utilidad) || 0) * (poolPercent / 100);

    const assignments = [];

    if (totalEligibleIncome > 0 && assignmentPool > 0) {
      instructoresProportions.forEach((p) => {
        const partner = (p?.miembro_que_asigna || "").trim();
        const partnerIncome = Number(p?.totalIncome) || 0;

        if (isExcludedFromAssignment(partner)) {
          return;
        }

        if (partner && partnerIncome > 0) {
          const proportion = partnerIncome / totalEligibleIncome;
          const commission = proportion * assignmentPool;
          const percentOfUtilidad = (commission / (Number(utilidad) || 1)) * 100;

          assignments.push({
            partner,
            income: partnerIncome,
            proportion: proportion * 100,
            commission,
            percentOfUtilidad,
          });
        }
      });
    }

    return {
      assignments,
      totalPool: assignmentPool,
      totalIncome: totalEligibleIncome,
      excludedIncome,
    };
  };

  computeUiUtilitiesTotals = () => {
    const { honorarios, amount } = this.state;
    const { utilidad } = this.props;

    const byPartner = {};
    const stagesByPartner = {};
    const assignmentsByPartner = {};

    Object.keys(honorarios || {}).forEach((key) => {
      const partner = (honorarios && honorarios[key]) || null;
      if (!partner) return;
      const qty = Number((amount && amount[key]) || 0) || 0;
      byPartner[partner] = (byPartner[partner] || 0) + qty;
      stagesByPartner[partner] = (stagesByPartner[partner] || 0) + qty;
    });

    const { assignments } = this.calculateInstructorAssignments();

    assignments.forEach(({ partner, commission }) => {
      if (partner && commission > 0) {
        byPartner[partner] = (byPartner[partner] || 0) + commission;
        assignmentsByPartner[partner] = (assignmentsByPartner[partner] || 0) + commission;
      }
    });

    const total = Object.values(byPartner).reduce((s, v) => s + v, 0);

    const backendTotal = (this.props.utilities || []).reduce(
      (s, u) => s + (Number(u.total_cantidad) || 0),
      0
    );

    console.log("[UTILIDADES] UI total vs Backend total:", {
      uiByPartner: byPartner,
      uiTotal: total,
      backendRows: this.props.utilities,
      backendTotal,
    });

    return {
      byPartner,
      stagesByPartner,
      assignmentsByPartner,
      total,
      backendTotal
    };
  };

  calculateCompleteDistribution = () => {
    const { honorarios, amount, percent } = this.state;
    const { utilidad } = this.props;

    const utilizadNum = Number(utilidad) || 0;

    const { assignments, totalPool, excludedIncome } = this.calculateInstructorAssignments();
    const { total: uiTotal, backendTotal } = this.computeUiUtilitiesTotals();

    const stageBreakdown = [];
    let totalStages = 0;

    Object.keys(honorarios).forEach((key) => {
      const partner = honorarios[key];
      const pct = Number(percent[key]) || 0;
      const amt = Number(amount[key]) || 0;

      if (partner && amt > 0) {
        stageBreakdown.push({
          stage: STAGE_DISPLAY_NAMES[key] || key,
          partner,
          percent: pct,
          amount: amt,
          category: this.getStageCategory(key),
        });
        totalStages += amt;
      }
    });

    const partnerSummary = {};

    stageBreakdown.forEach(({ partner, amount: amt, percent: pct }) => {
      if (!partnerSummary[partner]) {
        partnerSummary[partner] = {
          stages: [], stageTotal: 0, stagePercent: 0,
          assignments: [], assignmentTotal: 0, assignmentPercent: 0,
          grandTotal: 0, grandPercent: 0,
        };
      }
      partnerSummary[partner].stageTotal += amt;
      partnerSummary[partner].stagePercent += pct;
    });

    assignments.forEach(({ partner, commission, percentOfUtilidad }) => {
      if (!partnerSummary[partner]) {
        partnerSummary[partner] = {
          stages: [], stageTotal: 0, stagePercent: 0,
          assignments: [], assignmentTotal: 0, assignmentPercent: 0,
          grandTotal: 0, grandPercent: 0,
        };
      }
      partnerSummary[partner].assignmentTotal += commission;
      partnerSummary[partner].assignmentPercent += percentOfUtilidad;
    });

    Object.keys(partnerSummary).forEach((partner) => {
      const p = partnerSummary[partner];
      p.grandTotal = p.stageTotal + p.assignmentTotal;
      p.grandPercent = utilizadNum > 0 ? (p.grandTotal / utilizadNum) * 100 : 0;
    });

    const totalPartnersAmount = Object.values(partnerSummary).reduce(
      (sum, p) => sum + p.grandTotal, 0
    );
    const totalPartnersPercent = utilizadNum > 0 ? (totalPartnersAmount / utilizadNum) * 100 : 0;

    const lssiAssignment = assignments.find(a => a.partner === "LSSI");
    const lssiAssignmentAmount = lssiAssignment?.commission || 0;
    const lssiAssignmentPercent = lssiAssignment?.percentOfUtilidad || 0;

    const partnersExcludingLSSI = Object.entries(partnerSummary)
      .filter(([partner]) => partner !== "LSSI")
      .reduce((sum, [, p]) => sum + p.grandTotal, 0);

    const lssiRemaining = utilizadNum - partnersExcludingLSSI;
    const lssiRemainingPercent = utilizadNum > 0 ? (lssiRemaining / utilizadNum) * 100 : 0;

    return {
      utilidad: utilizadNum,
      stageBreakdown,
      totalStages,
      totalStagesPercent: utilizadNum > 0 ? (totalStages / utilizadNum) * 100 : 0,
      assignments,
      totalAssignments: totalPool,
      totalAssignmentsPercent: this.hasInstructors() ? getAssignmentPoolPercent() : 0,
      partnerSummary,
      totalPartnersAmount,
      totalPartnersPercent,
      partnersExcludingLSSI,
      partnersExcludingLSSIPercent: utilizadNum > 0 ? (partnersExcludingLSSI / utilizadNum) * 100 : 0,
      lssiAssignmentAmount,
      lssiAssignmentPercent,
      lssiRemaining,
      lssiRemainingPercent,
      uiTotal,
      backendTotal,
      excludedIncome,
    };
  };

  getStageCategory = (key) => {
    if (['contacto', 'cita'].includes(key)) return 'Preventa';
    if (['diagnostico', 'propuesta', 'cierre'].includes(key)) return 'Comercial';
    if (key === 'coaching') return 'Coaching';
    return 'Post Venta';
  };

  calculateComision = () => {
    const {
      estudiantes_curso_abierto,
      totalCostoInstructores,
      partidaCosto,
      admin_total_cost,
      total_cost_proyect_con_causa,
    } = this.props;

    const { utilidad_lssi } = this.calculateUtilidades() || {};
    if (typeof utilidad_lssi !== "number") {
      return { totalComissionByVendor: {}, totalUtilidadByAllVendors: 0 };
    }
    if (!Array.isArray(estudiantes_curso_abierto) || estudiantes_curso_abierto.length === 0) {
      return { totalComissionByVendor: {}, totalUtilidadByAllVendors: 0 };
    }

    const totalComissionByVendor = {};
    let totalSalesByAllVendors = 0;
    let totalUtilidadByAllVendors = 0;
    let totalLineItemCount = 0;

    estudiantes_curso_abierto.forEach(
      ({ partner_vendedor, precio, moneda, comision_vendedor }) => {
        if (partner_vendedor && precio && moneda) {
          const isLssiVendor = partner_vendedor.toLowerCase().includes("lssi");
          if (!totalComissionByVendor[partner_vendedor]) {
            totalComissionByVendor[partner_vendedor] = {
              totalPrecio: 0, moneda, lineitemcount: 0,
              costoInstructores: 0, totalComission: 0, Calculated_profit_by_vendor: 0,
            };
          }
          const v = totalComissionByVendor[partner_vendedor];
          v.totalPrecio += Number(precio) || 0;
          v.totalComission += isLssiVendor
            ? 0
            : ((Number(precio) || 0) * (Number(comision_vendedor) || 0)) / 100;
          v.lineitemcount += 1;
          totalSalesByAllVendors += Number(precio) || 0;
          totalLineItemCount += 1;
        }
      }
    );

    if (totalSalesByAllVendors === 0 || totalLineItemCount === 0) {
      return { totalComissionByVendor, totalUtilidadByAllVendors };
    }

    Object.entries(totalComissionByVendor).forEach(([vendor, v]) => {
      const isLssiVendor = vendor.toLowerCase().includes("lssi");
      if (isLssiVendor) {
        v.totalComission = 0; v.costoInstructores = 0; v.costoPartidas = 0;
        v.admin_total_cost = 0; v.total_cost_proyect_con_causa = 0; v.Calculated_profit_by_vendor = 0;
      } else {
        const proportion = v.totalPrecio / totalSalesByAllVendors;
        v.proportion = proportion;
        v.utilidad = proportion * 0.5 * utilidad_lssi;
        totalUtilidadByAllVendors += v.utilidad;
        v.costoInstructores = ((Number(totalCostoInstructores) || 0) / totalLineItemCount) * v.lineitemcount;
        v.costoPartidas = ((Number(partidaCosto) || 0) / totalLineItemCount) * v.lineitemcount;
        v.admin_total_cost = (Number(admin_total_cost) || 0) * proportion;
        v.total_cost_proyect_con_causa = (Number(total_cost_proyect_con_causa) || 0) * proportion;
        v.Calculated_profit_by_vendor =
          v.totalPrecio - v.totalComission - v.costoInstructores - v.costoPartidas - v.admin_total_cost - v.total_cost_proyect_con_causa;
      }
    });

    return { totalComissionByVendor, totalUtilidadByAllVendors };
  };

  calculateUtilidades() {
    const { filteredlider, utilidad } = this.props;
    let utilidad_lssi = 0;
    let utilidad_non_lssi = 0;
    let utilidad_non_lssi_name = "";

    if (Array.isArray(filteredlider) && filteredlider.length > 0) {
      const leader = filteredlider[0];
      utilidad_non_lssi_name = leader.lider;
      utilidad_non_lssi = (Number(leader.utilidad) / 100) * (Number(utilidad) || 0);
      utilidad_lssi = (1 - Number(leader.utilidad) / 100) * (Number(utilidad) || 0);
    }

    return { utilidad_lssi, utilidad_non_lssi, utilidad_non_lssi_name };
  }

  getUtilitiesEmailSummary = () => {
    const { honorarios, amount, partners } = this.state;
    let withEmail = 0, withoutEmail = 0, withAmount = 0, withoutAmount = 0;

    Object.keys(honorarios).forEach((key) => {
      const partnerName = honorarios[key];
      if (!partnerName) return;
      const partner = partners.find(p => p.partner === partnerName);
      if (partner?.email?.trim()) withEmail++; else withoutEmail++;
      if (Number(amount[key]) > 0) withAmount++; else withoutAmount++;
    });

    return { withEmail, withoutEmail, withAmount, withoutAmount, total: Object.values(honorarios).filter(v => v).length };
  };

  getAbiertoEmailSummary = () => {
    const { abierto_miembros } = this.props;
    const { totalComissionByVendor } = this.calculateComision();
    let withEmail = 0, withoutEmail = 0;

    Object.keys(totalComissionByVendor || {}).forEach((vendor) => {
      const member = (abierto_miembros || []).find(m => m.partner === vendor);
      if (member?.email?.trim()) withEmail++; else withoutEmail++;
    });

    return { withEmail, withoutEmail, total: Object.keys(totalComissionByVendor || {}).length };
  };

  sendUtilitiesEmail = async (e) => {
    e.preventDefault();

    const {
      proyectoNameSelected, proyectoCodeSelected, proyectoModalidadSelected,
      selectedProyecto, utilidad, primaryCurrency, profitInOriginalCurrency, utilidadUSD
    } = this.props;
    const { honorarios, amount, percent, partners, utilidadRowMeta } = this.state;

    // Confirm before firing — Salvador reported the button felt inert
    // because the only feedback was a toast that could scroll away.
    const uniqueRecipients = new Set();
    Object.values(honorarios).forEach((partnerName) => {
      if (!partnerName) return;
      const p = partners.find((x) => x.partner === partnerName);
      if (p?.email?.trim()) uniqueRecipients.add(partnerName);
    });
    const recipientCount = uniqueRecipients.size;
    const recipientList = Array.from(uniqueRecipients).join(", ");

    if (recipientCount === 0) {
      this.setState({
        emailSendResult: {
          kind: "warning",
          message: "No hay partners con email para notificar. Agrega el email en la sección Partners primero.",
        },
      });
      return;
    }

    const ok = window.confirm(
      `¿Enviar correo de utilidades a ${recipientCount} partner${recipientCount > 1 ? "s" : ""}?\n\n${recipientList}`
    );
    if (!ok) return;

    this.setState({ sendingEmails: true, emailSendResult: { kind: "info", message: `Enviando correos a ${recipientCount} partner(s)…` } });

    const toastId = toast.loading("Enviando correos de utilidades…");
    let sent = 0, failed = 0, skippedNoEmail = 0;
    const sentNames = [], failedNames = [], skippedNames = [];

    const convertToOriginalCurrency = (usdAmount) => {
      if (!profitInOriginalCurrency || !utilidadUSD || utilidadUSD === 0) return usdAmount;
      return (usdAmount / utilidadUSD) * profitInOriginalCurrency;
    };

    try {
      // 1) Collect stage-based entries per partner
      const partnerStages = {};
      for (const [key, partnerName] of Object.entries(honorarios)) {
        if (!partnerName) continue;
        const cantidadUSD = Number(amount[key]) || 0;
        if (cantidadUSD <= 0) continue;
        const cantidadOriginal = convertToOriginalCurrency(cantidadUSD);
        const meta = utilidadRowMeta[key];
        if (!partnerStages[partnerName]) partnerStages[partnerName] = [];
        partnerStages[partnerName].push({
          etapa: STAGE_DISPLAY_NAMES[key] || key,
          porcentaje: `${(Number(percent[key]) || 0).toFixed(2)}%`,
          monto: formatNumber(cantidadOriginal),
          // Preserve the raw number for arithmetic — formatNumber uses the
          // browser locale so parsing back the string is not safe under es-ES
          // or de-DE (they emit "1.234,56" and the parser would lose the decimals).
          montoRaw: cantidadOriginal,
          confirm_yes_url: meta?.confirm_yes_url || null,
          confirm_no_url: meta?.confirm_no_url || null,
        });
      }

      // 2) Calculate instructor assignment commissions per partner
      const { assignments } = this.calculateInstructorAssignments();
      const assignmentsByPartner = {};
      assignments.forEach(({ partner: assignPartner, commission, percentOfUtilidad }) => {
        if (commission > 0) {
          const commissionOriginal = convertToOriginalCurrency(commission);
          assignmentsByPartner[assignPartner] = {
            etapa: "Asignacion de Instructores",
            porcentaje: `${percentOfUtilidad.toFixed(2)}%`,
            monto: formatNumber(commissionOriginal),
            montoRaw: commissionOriginal,
          };
        }
      });

      // 3) Build combined entries and send emails
      // Collect all partners that have either stages or assignments
      const allPartners = new Set([...Object.keys(partnerStages), ...Object.keys(assignmentsByPartner)]);

      for (const partnerName of allPartners) {
        const partner = partners.find(p => p.partner === partnerName);
        const email = partner?.email?.trim();
        if (!email) { skippedNoEmail++; skippedNames.push(partnerName); continue; }

        const stages = partnerStages[partnerName] || [];
        const assignment = assignmentsByPartner[partnerName];

        // Combine stages + assignment into a single list of entries
        const allEntries = [...stages];
        if (assignment) {
          allEntries.push(assignment);
        }

        if (allEntries.length === 0) continue;

        const totalPartner = allEntries.reduce((sum, s) => sum + (Number(s.montoRaw) || 0), 0);
        const utilidadTotalOriginal = convertToOriginalCurrency(utilidad);
        const moneda = primaryCurrency || selectedProyecto?.moneda || "USD";
        const totalPercent = utilidadTotalOriginal > 0 ? ((totalPartner / utilidadTotalOriginal) * 100).toFixed(2) : "0.00";

        // Build HTML-formatted breakdown with black color for email readability
        const entriesHtml = allEntries.map(s =>
          `<span style="color:#111827;">${s.etapa}: ${s.porcentaje} → $${s.monto}</span>`
        ).join('<span style="color:#111827;"> | </span>');

        const utilidadDisplay = allEntries.length === 1
          ? `<span style="color:#111827;font-weight:bold;font-size:20px;">$${allEntries[0].monto} ${moneda}</span>`
          : `<span style="color:#111827;font-weight:bold;font-size:20px;">$${formatNumber(totalPartner)} ${moneda}</span><br/><span style="color:#111827;font-size:14px;">(${totalPercent}% de la utilidad total)</span>`;

        const porcentajeDisplay = allEntries.length === 1
          ? `<span style="color:#111827;">${allEntries[0].porcentaje}</span>`
          : `<span style="color:#111827;">${entriesHtml}</span>`;

        const tipoCount = stages.length + (assignment ? 1 : 0);

        // Build per-row Confirmar / Rechazar HTML block. One line per stage
        // (the assignment row has no confirm token — that record is created
        // by the recompute and not persisted with a token yet).
        const confirmRows = stages.filter(s => s.confirm_yes_url && s.confirm_no_url);
        const confirmLinksHtml = confirmRows.length > 0
          ? confirmRows.map(s => (
              `<div style="margin:6px 0;color:#111827;">` +
              `<strong>${s.etapa}:</strong> ` +
              `<a href="${s.confirm_yes_url}" style="color:#0f5132;font-weight:600;text-decoration:underline;">✓ Aceptar</a>` +
              ` &nbsp;·&nbsp; ` +
              `<a href="${s.confirm_no_url}" style="color:#842029;font-weight:600;text-decoration:underline;">✗ Rechazar</a>` +
              `</div>`
            )).join("")
          : "";

        const firstConfirm = confirmRows[0] || {};

        const emailParams = {
          to_email: email, cc_email: "bgonzalez@leansixsigmainstitute.org",
          from_name: "LSSI Fin", to_name: partnerName,
          reply_to: "bgonzalez@leansixsigmainstitute.org",
          partner: partnerName,
          tipo_utilidad: allEntries.length === 1 ? allEntries[0].etapa : `${tipoCount} conceptos`,
          porcentaje: porcentajeDisplay,
          utilidad: utilidadDisplay,
          moneda,
          proyectoNameFrom: proyectoNameSelected, proyecto_code: proyectoCodeSelected,
          modalidad: proyectoModalidadSelected, cliente: selectedProyecto?.cliente || "",
          producto: selectedProyecto?.producto || "",
          utilidad_total: formatNumber(utilidadTotalOriginal),
          link: `${window.location.origin}/project/${proyectoCodeSelected}`,
          // UPG-10: per-row confirm/decline links. `confirm_links_html`
          // is the full block for template use; `confirmar_url`/
          // `rechazar_url` are the first row's links as a fallback for
          // templates that only support single-URL buttons.
          confirm_links_html: confirmLinksHtml,
          confirmar_url: firstConfirm.confirm_yes_url || "",
          rechazar_url: firstConfirm.confirm_no_url || "",
        };

        try {
          await emailjs.send("service_avk65uk", "template_yief1i1", emailParams, "VBYWnQsgbxRBcGQ8N");
          sent++;
          sentNames.push(partnerName);
        } catch (err) { failed++; failedNames.push(partnerName); }
      }

      const parts = [];
      if (sent > 0) parts.push(`✓ ${sent} enviado${sent > 1 ? "s" : ""}`);
      if (failed > 0) parts.push(`✗ ${failed} fallido${failed > 1 ? "s" : ""}`);
      if (skippedNoEmail > 0) parts.push(`⚠ ${skippedNoEmail} sin email`);

      const summary = parts.join(" · ") || "No se enviaron correos";
      const kind = sent > 0 && failed === 0 ? "success" : failed > 0 ? "warning" : "info";

      toast.update(toastId, {
        render: summary, type: kind, isLoading: false, autoClose: 5000,
      });

      this.setState({
        emailSendResult: {
          kind,
          message: summary,
          sentNames, failedNames, skippedNames,
          timestamp: new Date().toLocaleString("es-MX"),
        },
      });
    } catch (err) {
      toast.update(toastId, { render: "Error al enviar correos.", type: "error", isLoading: false, autoClose: 5000 });
      this.setState({
        emailSendResult: {
          kind: "danger",
          message: `Error al enviar correos: ${err?.message || "desconocido"}`,
        },
      });
    } finally {
      this.setState({ sendingEmails: false });
    }
  };

  sendAbiertoEmail = async (e) => {
    e.preventDefault();
    this.setState({ sendingEmails: true });

    const headers = this.getHeaders();
    const { totalComissionByVendor } = this.calculateComision();
    const abierto_miembros_uni = (this.props.abierto_miembros || []).filter(
      (v, i, self) => i === self.findIndex((t) => t.partner === v.partner)
    );

    const toastId = toast.loading("Enviando correos de curso abierto…");
    let sent = 0, failed = 0, skippedNoEmail = 0, savedToDB = 0;

    try {
      const {
        proyectoNameSelected, proyectoCodeSelected, proyectoModalidadSelected,
        selectedProyecto, utilidad, profitInOriginalCurrency, utilidadUSD,
      } = this.props;

      const convertToOriginalCurrency = (usdAmount) => {
        if (!profitInOriginalCurrency || !utilidadUSD || utilidadUSD === 0) return usdAmount;
        return (usdAmount / utilidadUSD) * profitInOriginalCurrency;
      };
      const utilidadTotalOriginal = convertToOriginalCurrency(utilidad);

      for (const [vendor, data] of Object.entries(totalComissionByVendor)) {
        const member = abierto_miembros_uni.find((x) => x.partner === vendor);
        const email = member?.email?.trim();
        if (!email) { skippedNoEmail++; continue; }

        const emailParams = {
          to_email: email, cc_email: "bgonzalez@leansixsigmainstitute.org",
          from_name: "LSSI Fin", to_name: vendor,
          reply_to: "bgonzalez@leansixsigmainstitute.org",
          partner: vendor, utilidad: formatNumber(data.Calculated_profit_by_vendor / 2),
          moneda: data.moneda, proyectoNameFrom: proyectoNameSelected,
          proyecto_code: proyectoCodeSelected, modalidad: proyectoModalidadSelected,
          cliente: selectedProyecto?.cliente || "", producto: selectedProyecto?.producto || "",
          estudiantes: data.lineitemcount, ingreso: formatNumber(data.totalPrecio),
          instructores: formatNumber(data.costoInstructores), partidas: formatNumber(data.costoPartidas),
          admin: formatNumber(data.admin_total_cost), causa: formatNumber(data.total_cost_proyect_con_causa),
          comission: formatNumber(data.totalComission),
          utilidad_proyecto: formatNumber(utilidadTotalOriginal),
          link: `${window.location.origin}/project/${proyectoCodeSelected}`,
        };

        const dbParams = {
          partner: vendor, proyectoNameFrom: proyectoNameSelected,
          estudiantes: data.lineitemcount, ingreso: data.totalPrecio,
          instructores: data.costoInstructores, partidas: data.costoPartidas,
          admin: data.admin_total_cost, causa: data.total_cost_proyect_con_causa,
          comission: data.totalComission, utilidad: data.Calculated_profit_by_vendor / 2,
          utilidad_proyecto: utilidad, proyecto_code: proyectoCodeSelected, moneda: data.moneda,
        };

        try {
          await axios.post(API_URL_utilidad_partner_curso_abierto, dbParams, { headers });
          savedToDB++;
        } catch (postErr) {
          try {
            await axios.put(`${API_URL_utilidad_partner_curso_abierto}?proyecto_code=${proyectoCodeSelected}&partner=${vendor}`, dbParams, { headers });
            savedToDB++;
          } catch (putErr) { console.error("Failed to save to DB:", putErr); }
        }

        try {
          await emailjs.send("service_avk65uk", "template_yief1i1", emailParams, "VBYWnQsgbxRBcGQ8N");
          sent++;
        } catch (emailErr) { failed++; }
      }

      const parts = [];
      if (sent > 0) parts.push(`✓ ${sent} enviado${sent > 1 ? "s" : ""}`);
      if (failed > 0) parts.push(`✗ ${failed} fallido${failed > 1 ? "s" : ""}`);
      if (skippedNoEmail > 0) parts.push(`⚠ ${skippedNoEmail} sin email`);
      if (savedToDB > 0) parts.push(`💾 ${savedToDB} guardado${savedToDB > 1 ? "s" : ""}`);

      toast.update(toastId, {
        render: parts.join(", ") || "No se enviaron correos",
        type: sent > 0 && failed === 0 ? "success" : failed > 0 ? "warning" : "info",
        isLoading: false, autoClose: 5000,
      });
    } catch (error) {
      toast.update(toastId, { render: "Error al enviar correos.", type: "error", isLoading: false, autoClose: 5000 });
    } finally {
      this.setState({ sendingEmails: false });
    }
  };

  // ✅ Download Excel — Cerrado
  downloadUtilitiesExcel = () => {
    const { honorarios, amount, percent } = this.state;
    const { proyectoNameSelected, proyectoCodeSelected, selectedProyecto, utilidad, primaryCurrency, profitInOriginalCurrency, utilidadUSD } = this.props;

    const convertToOriginalCurrency = (usdAmount) => {
      if (!profitInOriginalCurrency || !utilidadUSD || utilidadUSD === 0) return usdAmount;
      return (usdAmount / utilidadUSD) * profitInOriginalCurrency;
    };

    const currency = primaryCurrency || selectedProyecto?.moneda || "USD";
    const utilidadTotalOriginal = convertToOriginalCurrency(utilidad);

    const stageRows = [];
    for (const [key, partnerName] of Object.entries(honorarios)) {
      if (!partnerName) continue;
      const cantidadUSD = Number(amount[key]) || 0;
      if (cantidadUSD <= 0) continue;
      stageRows.push({
        Partner: partnerName, Categoría: this.getStageCategory(key),
        Etapa: STAGE_DISPLAY_NAMES[key] || key,
        "Porcentaje (%)": Number((Number(percent[key]) || 0).toFixed(2)),
        [`Monto (${currency})`]: Number(convertToOriginalCurrency(cantidadUSD).toFixed(2)),
        Proyecto: proyectoNameSelected, Código: proyectoCodeSelected,
        Moneda: currency, "Utilidad Total": Number(utilidadTotalOriginal.toFixed(2)),
      });
    }

    if (stageRows.length === 0) { toast.info("No hay datos para exportar."); return; }

    const wsStages = XLSX.utils.json_to_sheet(stageRows);
    wsStages["!cols"] = Object.keys(stageRows[0]).map((k) => ({ wch: Math.max(k.length, 14) }));

    const dist = this.calculateCompleteDistribution();
    const partnerRows = [];

    Object.entries(dist.partnerSummary).filter(([partner]) => partner !== "LSSI").sort((a, b) => b[1].grandTotal - a[1].grandTotal)
      .forEach(([partner, data]) => {
        partnerRows.push({
          Partner: partner, "Etapas (%)": Number(data.stagePercent.toFixed(2)),
          [`Etapas (${currency})`]: Number(convertToOriginalCurrency(data.stageTotal).toFixed(2)),
          "Asignación (%)": Number(data.assignmentPercent.toFixed(2)),
          [`Asignación (${currency})`]: Number(convertToOriginalCurrency(data.assignmentTotal).toFixed(2)),
          "Total (%)": Number(data.grandPercent.toFixed(2)),
          [`Total (${currency})`]: Number(convertToOriginalCurrency(data.grandTotal).toFixed(2)),
        });
      });

    partnerRows.push({
      Partner: "LSSI", "Etapas (%)": 0, [`Etapas (${currency})`]: 0,
      "Asignación (%)": Number(dist.lssiAssignmentPercent.toFixed(2)),
      [`Asignación (${currency})`]: Number(convertToOriginalCurrency(dist.lssiAssignmentAmount).toFixed(2)),
      "Total (%)": Number(dist.lssiRemainingPercent.toFixed(2)),
      [`Total (${currency})`]: Number(convertToOriginalCurrency(dist.lssiRemaining).toFixed(2)),
    });

    partnerRows.push({
      Partner: "TOTAL", "Etapas (%)": Number(dist.totalStagesPercent.toFixed(2)),
      [`Etapas (${currency})`]: Number(convertToOriginalCurrency(dist.totalStages).toFixed(2)),
      "Asignación (%)": Number((dist.totalAssignmentsPercent || 0).toFixed(2)),
      [`Asignación (${currency})`]: Number(convertToOriginalCurrency(dist.totalAssignments || 0).toFixed(2)),
      "Total (%)": 100,
      [`Total (${currency})`]: Number(utilidadTotalOriginal.toFixed(2)),
    });

    const wsPartners = XLSX.utils.json_to_sheet(partnerRows);
    wsPartners["!cols"] = [{ wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 12 }, { wch: 18 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsStages, "Desglose por Etapa");
    XLSX.utils.book_append_sheet(wb, wsPartners, "Distribución por Partner");

    // Instructor assignment detail (12.5% pool) — mirrors the
    // "Detalle de Asignación de Instructores" table in the UI. Skipped when
    // there are no instructors, since in that case the 12.5% collapses into
    // Coaching (which shows up in the Etapas sheet instead).
    if (dist.assignments && dist.assignments.length > 0) {
      const assignmentRows = dist.assignments.map((item) => ({
        "Partner que Asigna": item.partner,
        [`Ingreso Asignado (${currency})`]: Number(convertToOriginalCurrency(item.income).toFixed(2)),
        "Proporción (%)": Number(item.proportion.toFixed(2)),
        "% de Utilidad": Number(item.percentOfUtilidad.toFixed(2)),
        [`Comisión (${currency})`]: Number(convertToOriginalCurrency(item.commission).toFixed(2)),
      }));

      assignmentRows.push({
        "Partner que Asigna": "TOTAL",
        [`Ingreso Asignado (${currency})`]: Number(convertToOriginalCurrency(dist.assignments.reduce((s, a) => s + a.income, 0)).toFixed(2)),
        "Proporción (%)": 100,
        "% de Utilidad": Number((dist.totalAssignmentsPercent || 0).toFixed(2)),
        [`Comisión (${currency})`]: Number(convertToOriginalCurrency(dist.totalAssignments || 0).toFixed(2)),
      });

      const wsAssignments = XLSX.utils.json_to_sheet(assignmentRows);
      wsAssignments["!cols"] = Object.keys(assignmentRows[0]).map((k) => ({ wch: Math.max(k.length, 18) }));
      XLSX.utils.book_append_sheet(wb, wsAssignments, "Asignación Instructores");
    }

    XLSX.writeFile(wb, `Rentabilidad_${proyectoCodeSelected || "proyecto"}.xlsx`);
    toast.success("📥 Excel descargado exitosamente.");
  };

  // ✅ Download Excel — Abierto
  downloadAbiertoExcel = () => {
    const { totalComissionByVendor } = this.calculateComision();
    const { proyectoNameSelected, proyectoCodeSelected, utilidad, profitInOriginalCurrency, utilidadUSD } = this.props;

    if (!totalComissionByVendor || Object.keys(totalComissionByVendor).length === 0) { toast.info("No hay datos para exportar."); return; }

    const convertToOriginalCurrency = (usdAmount) => {
      if (!profitInOriginalCurrency || !utilidadUSD || utilidadUSD === 0) return usdAmount;
      return (usdAmount / utilidadUSD) * profitInOriginalCurrency;
    };
    const utilidadTotalOriginal = convertToOriginalCurrency(utilidad);

    const rows = Object.entries(totalComissionByVendor).map(([vendor, v]) => ({
      Partner: vendor, Estudiantes: v.lineitemcount, Ingreso: Number((v.totalPrecio || 0).toFixed(2)),
      "Costo Instructores": Number((v.costoInstructores || 0).toFixed(2)),
      "Costo Partidas": Number((v.costoPartidas || 0).toFixed(2)),
      Admin: Number((v.admin_total_cost || 0).toFixed(2)),
      Causa: Number((v.total_cost_proyect_con_causa || 0).toFixed(2)),
      Comisión: Number((v.totalComission || 0).toFixed(2)),
      "Utilidad Proyecto": Number((v.Calculated_profit_by_vendor || 0).toFixed(2)),
      "Utilidad Partner": Number(((v.Calculated_profit_by_vendor || 0) / 2).toFixed(2)),
      Moneda: v.moneda || "USD", Proyecto: proyectoNameSelected,
      Código: proyectoCodeSelected, "Utilidad Total Proyecto": Number(utilidadTotalOriginal.toFixed(2)),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((k) => ({ wch: Math.max(k.length, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rentabilidad Abierto");
    XLSX.writeFile(wb, `Rentabilidad_Abierto_${proyectoCodeSelected || "proyecto"}.xlsx`);
    toast.success("📥 Excel descargado exitosamente.");
  };

  convertUSDToOriginal = (usdAmount) => {
    const { profitInOriginalCurrency, utilidadUSD } = this.props;
    if (!profitInOriginalCurrency || !utilidadUSD || utilidadUSD === 0) return null;
    return (usdAmount / utilidadUSD) * profitInOriginalCurrency;
  };

  formatAmountWithCurrency = (usdAmount) => {
    const { primaryCurrency } = this.props;
    const originalAmount = this.convertUSDToOriginal(usdAmount);

    if (originalAmount !== null && primaryCurrency && primaryCurrency !== 'USD') {
      return (
        <>
          <small className="text-muted d-block">{primaryCurrency} {formatNumber(originalAmount)}</small>
          <span>${formatNumber(usdAmount)} USD</span>
        </>
      );
    }
    return <span>${formatNumber(usdAmount)}</span>;
  };

  // ✅ Render CFO Distribution Summary
  renderDistributionSummary = () => {
    const dist = this.calculateCompleteDistribution();
    const { utilidad } = this.props;
    if (!utilidad || utilidad <= 0) return null;

    const poolPercent = this.hasInstructors() ? getAssignmentPoolPercent() : 0;

    return (
      <Card className="mb-4 shadow-sm">
        <CardHeader className="bg-primary text-white">
          <h5 className="mb-0">📊 Resumen de Distribución de Utilidad</h5>
        </CardHeader>
        <CardBody>

          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="text-muted mb-2">UTILIDAD TOTAL DEL PROYECTO</h6>
            <h2 className="text-primary mb-0">{this.formatAmountWithCurrency(dist.utilidad)}</h2>
          </div>

          <h5 className="mb-3">📋 Asignación de Instructores - Distribución por Partner</h5>
          <Table responsive bordered hover className="mb-4">
            <thead className="table-dark">
              <tr>
                <th>Partner</th>
                <th className="text-center">Etapas (%)</th>
                <th className="text-end">Etapas ($)</th>
                <th className="text-center">Asignación (%)</th>
                <th className="text-end">Asignación ($)</th>
                <th className="text-center bg-warning text-dark">Total (%)</th>
                <th className="text-end bg-warning text-dark">Total ($)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(dist.partnerSummary).filter(([partner]) => partner !== "LSSI").sort((a, b) => b[1].grandTotal - a[1].grandTotal)
                .map(([partner, data]) => (
                  <tr key={partner}>
                    <td><strong>{partner}</strong></td>
                    <td className="text-center">{data.stagePercent.toFixed(2)}%</td>
                    <td className="text-end">{this.formatAmountWithCurrency(data.stageTotal)}</td>
                    <td className="text-center">{data.assignmentPercent.toFixed(2)}%</td>
                    <td className="text-end">{this.formatAmountWithCurrency(data.assignmentTotal)}</td>
                    <td className="text-center bg-warning-subtle"><strong>{data.grandPercent.toFixed(2)}%</strong></td>
                    <td className="text-end bg-warning-subtle"><strong>{this.formatAmountWithCurrency(data.grandTotal)}</strong></td>
                  </tr>
                ))}
              <tr className="table-success">
                <td><strong>LSSI</strong></td>
                <td className="text-center">—</td><td className="text-end">—</td>
                <td className="text-center">{dist.lssiAssignmentPercent.toFixed(2)}%</td>
                <td className="text-end">{this.formatAmountWithCurrency(dist.lssiAssignmentAmount)}</td>
                <td className="text-center"><strong>{dist.lssiRemainingPercent.toFixed(2)}%</strong></td>
                <td className="text-end"><strong>{this.formatAmountWithCurrency(dist.lssiRemaining)}</strong></td>
              </tr>
              <tr className="table-dark">
                <td><strong>TOTAL</strong></td>
                <td className="text-center"><strong>{dist.totalStagesPercent.toFixed(2)}%</strong></td>
                <td className="text-end"><strong>{this.formatAmountWithCurrency(dist.totalStages)}</strong></td>
                <td className="text-center"><strong>{poolPercent.toFixed(2)}%</strong></td>
                <td className="text-end"><strong>{this.formatAmountWithCurrency(dist.totalAssignments)}</strong></td>
                <td className="text-center"><strong>100.00%</strong></td>
                <td className="text-end"><strong>{this.formatAmountWithCurrency(dist.utilidad)}</strong></td>
              </tr>
            </tbody>
          </Table>

          <h5 className="mb-3">📌 Detalle de Etapas (37.5%)</h5>
          <Table responsive bordered size="sm" className="mb-4">
            <thead className="table-secondary">
              <tr><th>Categoría</th><th>Etapa</th><th>Partner Asignado</th><th className="text-center">%</th><th className="text-end">Monto</th></tr>
            </thead>
            <tbody>
              {dist.stageBreakdown.map((item, idx) => (
                <tr key={idx}>
                  <td><span className={`badge bg-${item.category === 'Preventa' ? 'primary' : item.category === 'Comercial' ? 'success' : item.category === 'Coaching' ? 'info' : 'warning'}`}>{item.category}</span></td>
                  <td>{item.stage}</td><td>{item.partner}</td>
                  <td className="text-center">{item.percent.toFixed(2)}%</td>
                  <td className="text-end">{this.formatAmountWithCurrency(item.amount)}</td>
                </tr>
              ))}
              <tr className="table-secondary">
                <td colSpan="3"><strong>Subtotal Etapas</strong></td>
                <td className="text-center"><strong>{dist.totalStagesPercent.toFixed(2)}%</strong></td>
                <td className="text-end"><strong>{this.formatAmountWithCurrency(dist.totalStages)}</strong></td>
              </tr>
            </tbody>
          </Table>

          {dist.assignments.length > 0 && (
            <>
              <h5 className="mb-3">👥 Detalle de Asignación de Instructores ({poolPercent}%)</h5>
              <Table responsive bordered size="sm" className="mb-4">
                <thead className="table-secondary">
                  <tr><th>Partner que Asigna</th><th className="text-end">Ingreso Asignado (USD)</th><th className="text-center">Proporción</th><th className="text-center">% de Utilidad</th><th className="text-end">Comisión</th></tr>
                </thead>
                <tbody>
                  {dist.assignments.map((item, idx) => (
                    <tr key={idx} className={item.partner === "LSSI" ? "table-success" : ""}>
                      <td><strong>{item.partner}</strong></td>
                      <td className="text-end">{this.formatAmountWithCurrency(item.income)}</td>
                      <td className="text-center">{item.proportion.toFixed(2)}%</td>
                      <td className="text-center">{item.percentOfUtilidad.toFixed(2)}%</td>
                      <td className="text-end">{this.formatAmountWithCurrency(item.commission)}</td>
                    </tr>
                  ))}
                  <tr className="table-secondary">
                    <td><strong>Subtotal Asignación</strong></td><td className="text-end">—</td>
                    <td className="text-center"><strong>100.00%</strong></td>
                    <td className="text-center"><strong>{poolPercent.toFixed(2)}%</strong></td>
                    <td className="text-end"><strong>{this.formatAmountWithCurrency(dist.totalAssignments)}</strong></td>
                  </tr>
                </tbody>
              </Table>
            </>
          )}

          <div className="row mt-4">
            <div className="col-md-6">
              <Card className="border-warning"><CardBody className="text-center">
                <h6 className="text-muted">TOTAL PARTNERS (excl. LSSI)</h6>
                <h3 className="text-warning">{this.formatAmountWithCurrency(dist.partnersExcludingLSSI)}</h3>
                <small className="text-muted">{dist.partnersExcludingLSSIPercent.toFixed(2)}% de utilidad</small>
              </CardBody></Card>
            </div>
            <div className="col-md-6">
              <Card className="border-success"><CardBody className="text-center">
                <h6 className="text-muted">TOTAL LSSI</h6>
                <h3 className="text-success">{this.formatAmountWithCurrency(dist.lssiRemaining)}</h3>
                <small className="text-muted">{dist.lssiRemainingPercent.toFixed(2)}% de utilidad</small>
              </CardBody></Card>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  renderTableRows = (items, categoryClass) => {
    const { partners, percent, amount, honorarios, utilidadRowMeta } = this.state;
    const partnerOptions = (partners || []).map((p) => ({ value: p.partner, label: p.partner }));

    const statusBadge = (status) => {
      const map = {
        confirmed: { label: "Aceptado", color: "#0f5132", bg: "#d1e7dd" },
        declined:  { label: "Rechazado", color: "#842029", bg: "#f8d7da" },
        pending:   { label: "Pendiente", color: "#664d03", bg: "#fff3cd" },
      };
      const s = map[status] || map.pending;
      return (
        <span style={{
          display: "inline-block", padding: "3px 8px", borderRadius: 4,
          fontSize: "0.75em", fontWeight: 600, color: s.color, backgroundColor: s.bg,
          whiteSpace: "nowrap",
        }}>
          {s.label}
        </span>
      );
    };

    return items.map((item, index) => {
      const currentValue = honorarios[item.name] || "";
      const selectedOption = currentValue ? { value: currentValue, label: currentValue } : null;
      const meta = utilidadRowMeta[item.name];
      const hasPartner = Boolean(currentValue);

      return (
        <tr key={index} className={categoryClass}>
          <td>{item.category}</td>
          <td>{STAGE_DISPLAY_NAMES[item.name] || item.name}</td>
          <td style={{ minWidth: "250px" }}>
            <Select options={partnerOptions} value={selectedOption}
              onChange={(option) => this.onSelectChange(option, item.name)}
              placeholder={`Seleccionar...`} isClearable isSearchable
              noOptionsMessage={() => "No hay partners"}
              styles={{ control: (base) => ({ ...base, minHeight: "38px" }), menu: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </td>
          <td className="text-center">{(Number(percent[item.name]) || 0).toFixed(2)}%</td>
          <td className="text-end">{this.formatAmountWithCurrency(Number(amount[item.name]) || 0)}</td>
          <td className="text-center">
            {hasPartner && meta ? statusBadge(meta.confirmation_status) : (
              <span style={{ color: "#9ca3af", fontSize: "0.75em" }}>—</span>
            )}
          </td>
        </tr>
      );
    });
  };

  generateCommissionTable = () => {
    const { totalComissionByVendor, totalUtilidadByAllVendors } = this.calculateComision();
    if (!totalComissionByVendor || Object.keys(totalComissionByVendor).length === 0) return null;

    return (
      <div>
        <h5>Utilidad por Partner (Curso Abierto)</h5>
        <Table responsive hover striped bordered>
          <thead className="table-dark">
            <tr><th>Partner</th><th>Estudiantes</th><th>Ingreso</th><th>Costo Instructores</th><th>Costo Partidas</th><th>Admin</th><th>Causa</th><th>Comisión</th><th>Utilidad Proyecto</th><th>Utilidad Partner</th></tr>
          </thead>
          <tbody>
            {Object.entries(totalComissionByVendor).map(([vendor, v]) => (
              <tr key={vendor}>
                <td>{vendor}</td><td>{v.lineitemcount}</td>
                <td>{this.formatAmountWithCurrency(v.totalPrecio)}</td>
                <td>{this.formatAmountWithCurrency(v.costoInstructores)}</td>
                <td>{this.formatAmountWithCurrency(v.costoPartidas)}</td>
                <td>{this.formatAmountWithCurrency(v.admin_total_cost)}</td>
                <td>{this.formatAmountWithCurrency(v.total_cost_proyect_con_causa)}</td>
                <td>{this.formatAmountWithCurrency(v.totalComission)}</td>
                <td>{this.formatAmountWithCurrency(v.Calculated_profit_by_vendor)}</td>
                <td><strong>{this.formatAmountWithCurrency(v.Calculated_profit_by_vendor / 2)}</strong></td>
              </tr>
            ))}
          </tbody>
        </Table>
        <h5>Total utilidad partners: {this.formatAmountWithCurrency(totalUtilidadByAllVendors)}</h5>
      </div>
    );
  };

  renderDistributionModelInfo = () => {
    const userEmail = getCurrentUser() || '';
    if (userEmail !== 'salvador@lidigo.io') return null;

    return (
      <Alert color="info" className="mb-3">
        <strong>📘 Modelo de Distribución de Utilidades (Información Interna)</strong>
        <ul className="mb-0 mt-2">
          <li><strong>37.5%</strong> - Etapas del Pipeline (Preventa, Comercial, Post Venta)</li>
          <li><strong>12.5%</strong> - Asignación de Instructores (según proporción de trabajo)</li>
          <li><strong>50%</strong> - LSSI restante</li>
        </ul>
        <small className="text-muted d-block mt-2">ℹ️ Esta información solo es visible para ti</small>
      </Alert>
    );
  };

  render() {
    const { proyectoModalidadSelected, utilidad, totalCantidad } = this.props;
    const { sendingEmails } = this.state;
    const { utilidad_lssi, utilidad_non_lssi, utilidad_non_lssi_name } = this.calculateUtilidades();
    const { totalUtilidadByAllVendors } = this.calculateComision();
    const dist = this.calculateCompleteDistribution();

    const { filteredlider } = this.props;
    let lssiAfterLeader = dist.lssiRemaining;
    let leaderShare = 0;
    let leaderName = "";

    if (Array.isArray(filteredlider) && filteredlider.length > 0) {
      const leader = filteredlider[0];
      if (leader && Number(leader.utilidad) > 0) {
        leaderName = leader.lider;
        leaderShare = (Number(leader.utilidad) / 100) * dist.lssiRemaining;
        lssiAfterLeader = dist.lssiRemaining - leaderShare;
      }
    }

    const preventaItems = [{ category: "Preventa", name: "contacto" }, { category: "Preventa", name: "cita" }];
    const comercialItems = [{ category: "Comercial", name: "diagnostico" }, { category: "Comercial", name: "propuesta" }, { category: "Comercial", name: "cierre" }];
    const postVentaItems = [{ category: "Post Venta", name: "kickoff" }, { category: "Post Venta", name: "seguimiento" }, { category: "Post Venta", name: "reunioncierre" }];
    const coachingItems = [{ category: "Coaching", name: "coaching" }];

    const utilitiesSummary = this.getUtilitiesEmailSummary();
    const hasUtilitiesToEmail = utilitiesSummary.withEmail > 0;
    const abiertoSummary = this.getAbiertoEmailSummary();
    const hasAbiertoToEmail = abiertoSummary.withEmail > 0;

    return (
      <div>
        {/* Stage breakdown - shown for ALL modalities */}
        {this.renderDistributionModelInfo()}
        <Card className="mb-4">
          <CardHeader><h5 className="mb-0">⚙️ Asignación de Etapas del Pipeline</h5></CardHeader>
          <CardBody>
            <Table responsive hover bordered>
              <thead className="table-light">
                <tr><th>Categoría</th><th>Etapa</th><th>Partner Asignado</th><th className="text-center">%</th><th className="text-end">Monto (USD)</th><th className="text-center">Estatus</th></tr>
              </thead>
              <tbody>
                {this.renderTableRows(preventaItems, "table-primary")}
                {this.renderTableRows(comercialItems, "table-success")}
                {this.renderTableRows(postVentaItems, "table-warning")}
                {this.renderTableRows(coachingItems, "table-info")}
              </tbody>
            </Table>
            <div className="d-flex align-items-center gap-2 mt-3">
              <Button color={hasUtilitiesToEmail ? "success" : "secondary"} onClick={this.sendUtilitiesEmail} disabled={sendingEmails || !hasUtilitiesToEmail}>
                {sendingEmails ? "Enviando..." : hasUtilitiesToEmail ? `📧 Notificar a ${utilitiesSummary.withEmail} partner(s)` : "Sin partners para notificar"}
              </Button>
              <Button color="info" outline onClick={this.downloadUtilitiesExcel}>📥 Descargar Excel</Button>
              {!hasUtilitiesToEmail && utilitiesSummary.total > 0 && (<span className="text-warning">⚠️ Agrega emails a los partners</span>)}
            </div>
            {this.state.emailSendResult && (
              <Alert
                color={this.state.emailSendResult.kind}
                className="mt-3 mb-0"
                toggle={() => this.setState({ emailSendResult: null })}
              >
                <div style={{ fontWeight: 600 }}>{this.state.emailSendResult.message}</div>
                {this.state.emailSendResult.timestamp && (
                  <div style={{ fontSize: "0.85em", opacity: 0.85, marginTop: 4 }}>
                    {this.state.emailSendResult.timestamp}
                  </div>
                )}
                {(this.state.emailSendResult.sentNames?.length > 0) && (
                  <div style={{ fontSize: "0.85em", marginTop: 6 }}>
                    <strong>Enviados:</strong> {this.state.emailSendResult.sentNames.join(", ")}
                  </div>
                )}
                {(this.state.emailSendResult.failedNames?.length > 0) && (
                  <div style={{ fontSize: "0.85em", marginTop: 4 }}>
                    <strong>Fallidos:</strong> {this.state.emailSendResult.failedNames.join(", ")}
                  </div>
                )}
                {(this.state.emailSendResult.skippedNames?.length > 0) && (
                  <div style={{ fontSize: "0.85em", marginTop: 4 }}>
                    <strong>Sin email:</strong> {this.state.emailSendResult.skippedNames.join(", ")}
                  </div>
                )}
              </Alert>
            )}
          </CardBody>
        </Card>

        {this.renderDistributionSummary()}
        <ToastContainer />
        {this.state.isSaving && (<Progress animated color="info" value={100} style={{ height: "10px" }} />)}

        {/* Commission table - only for Abierto modalities */}
        {proyectoModalidadSelected && proyectoModalidadSelected.includes("Abierto") && (
          <div>
            {this.generateCommissionTable()}
            <div className="d-flex align-items-center gap-2 mb-3 mt-3">
              <Button color={hasAbiertoToEmail ? "success" : "secondary"} onClick={this.sendAbiertoEmail} disabled={sendingEmails || !hasAbiertoToEmail}>
                {sendingEmails ? "Enviando..." : hasAbiertoToEmail ? `📧 Notificar a ${abiertoSummary.withEmail} partner(s)` : "Sin partners para notificar"}
              </Button>
              <Button color="info" outline onClick={this.downloadAbiertoExcel}>📥 Descargar Excel</Button>
              {!hasAbiertoToEmail && abiertoSummary.total > 0 && (<span className="text-warning">⚠️ Agrega emails a los partners</span>)}
            </div>
          </div>
        )}

        {this.props.isAdmin && (
          <Card className="mt-4 border-dark">
            <CardBody>
              <div className="row">
                <div className="col-md-6"><h4 className="text-success">Utilidad LSSI: {this.formatAmountWithCurrency(lssiAfterLeader)}</h4></div>
                {leaderName && leaderShare > 0 && (<div className="col-md-6"><h4 className="text-warning">Utilidad Líder ({leaderName}): {this.formatAmountWithCurrency(leaderShare)}</h4></div>)}
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    );
  }
}

export default React.forwardRef((props, ref) => (
  <HonorariosList {...props} forwardedRef={ref} />
));